import React, { useMemo } from 'react';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';

export type RankReqRankColumn = {
  id: number;
  label: string;
};

export type RankReqByNatMatrixRow = {
  key: string;
  category: string;
  certificate: string;
  categoryOrder: number;
  certOrder: number;
  /** rankId → mandatory Yes */
  byRank: Record<number, boolean>;
};

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function TableHead({ ranks }: { ranks: RankReqRankColumn[] }) {
  return (
    <table className="rr-print-table rr-print-table-head rr-print-by-nat">
      <thead>
        <tr>
          <th className="rr-print-col-no">No.</th>
          <th className="rr-print-col-cat">Category</th>
          <th className="rr-print-col-cert">Certificate</th>
          {ranks.map((r) => (
            <th key={r.id} className="rr-print-col-rank" title={r.label}>
              {r.label}
            </th>
          ))}
        </tr>
      </thead>
    </table>
  );
}

function LineRow({
  row,
  index,
  ranks,
}: {
  row: RankReqByNatMatrixRow;
  index: number;
  ranks: RankReqRankColumn[];
}) {
  return (
    <table className="rr-print-table rr-print-row-table rr-print-by-nat">
      <tbody>
        <tr>
          <td className="rr-print-col-no">{index + 1}</td>
          <td className="rr-print-col-cat">{dash(row.category)}</td>
          <td className="rr-print-col-cert">{dash(row.certificate)}</td>
          {ranks.map((r) => {
            const yes = !!row.byRank[r.id];
            return (
              <td key={r.id} className={`rr-print-col-rank${yes ? ' rr-print-cell-yes' : ''}`}>
                {yes ? <span className="rr-print-pill rr-print-pill-yes">Yes</span> : '—'}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

export type RankRequirementsByNationalityReportProps = {
  rows: RankReqByNatMatrixRow[];
  ranks: RankReqRankColumn[];
  vesselName: string;
  nationalityLabel: string;
};

export const RankRequirementsByNationalityReport: React.FC<
  RankRequirementsByNationalityReportProps
> = ({ rows, ranks, vesselName, nationalityLabel }) => {
  const firstHeader = useMemo(
    () => (
      <div className="rr-print-flow-header">
        <div className="rr-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="Document Requirements" formId="By nationality" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField label="Nationality" value={nationalityLabel} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead ranks={ranks} />
      </div>
    ),
    [nationalityLabel, ranks, vesselName],
  );

  const continueHeader = useMemo(
    () => (
      <div className="rr-print-flow-header">
        <div className="rr-print-continue-bar">
          <span className="rr-print-continue-title">Document Requirements</span>
          <span className="rr-print-continue-meta">
            {vesselName} · {nationalityLabel}
          </span>
          <span className="rr-print-continue-hint">continued</span>
        </div>
        <TableHead ranks={ranks} />
      </div>
    ),
    [nationalityLabel, ranks, vesselName],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Mandatory (Yes) requirements for one nationality across ranks. Columns are ranks; rows are
          certificates. “—” means not mandatory for that rank.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const colSpan = 3 + Math.max(ranks.length, 1);

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="rr-print-table rr-print-row-table">
            <tbody>
              <tr>
                <td colSpan={colSpan} className="rr-print-empty">
                  No mandatory (Yes) requirements for this vessel and nationality.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => (
        <LineRow key={row.key} row={row} index={i} ranks={ranks} />
      )),
      itemKeys: rows.map((row) => row.key),
    };
  }, [colSpan, ranks, rows]);

  return (
    <ReportFlowPages
      className="rank-requirements-print rank-requirements-print-by-nat"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default RankRequirementsByNationalityReport;
